import { api } from './apiClient';
import {
  Dashboard,
  DashboardDetail,
  WidgetDescriptor,
  LayoutSavePayload,
  UserPreferences,
  Favorite,
} from '../dashboard/types';

/**
 * Dashboards, layouts, the widget registry, preferences and favourites.
 *
 * apiClient auto-unwraps the `{ status, data }` envelope for all five verbs
 * (see apiClient.ts). The `r?.data ?? r` below is now belt-and-braces rather
 * than load-bearing — harmless either way, since `data` is undefined on an
 * already-unwrapped body and the `??` falls through to `r` unchanged.
 */
export const dashboardService = {
  list: (): Promise<{ dashboards: Dashboard[]; count: number }> => api.get('/dashboards'),

  /**
   * One dashboard with its widgets, normalised to the client shape.
   *
   * The server returns `{ dashboard, widgets }` with each row's registry
   * metadata under `widget`, and omits `key`/`version`/`defaultSize` from it
   * (they are already on the row). The client type wants a flat
   * `DashboardDetail` with a complete `descriptor`, so the adaptation happens
   * here — once — rather than in each of the 34 widgets.
   *
   * Getting this wrong is silent and total: `WidgetRenderer` falls back to the
   * "no longer available" tile when `descriptor` is missing, so a mismatched
   * key renders an entire dashboard of placeholders with no error anywhere.
   */
  get: async (id: number): Promise<DashboardDetail> => {
    const res: any = await api.get(`/dashboards/${id}`);
    const dash = res?.dashboard ?? res ?? {};
    const rows: any[] = res?.widgets ?? dash?.widgets ?? [];

    return {
      ...dash,
      widgets: rows.map((row) => {
        const meta = row.widget ?? row.descriptor ?? {};
        return {
          id: row.id,
          widgetKey: row.widgetKey,
          widgetVersion: row.widgetVersion ?? 1,
          gridX: row.gridX ?? 0,
          gridY: row.gridY ?? 0,
          gridW: row.gridW ?? meta.minSize?.w ?? 4,
          gridH: row.gridH ?? meta.minSize?.h ?? 4,
          config: row.config ?? {},
          isCollapsed: Boolean(row.isCollapsed),
          isPinned: Boolean(row.isPinned),
          descriptor: {
            key: row.widgetKey,
            version: row.widgetVersion ?? 1,
            title: meta.title ?? row.widgetKey,
            category: meta.category ?? 'general',
            // The row's own geometry is the truest "default" for a placed
            // widget — it is what this user last saved.
            defaultSize: { w: row.gridW ?? 4, h: row.gridH ?? 4 },
            minSize: meta.minSize ?? { w: 2, h: 2 },
            maxSize: meta.maxSize ?? { w: 12, h: 12 },
            dataEndpoint: meta.dataEndpoint,
            description: meta.description,
          },
        };
      }),
    } as DashboardDetail;
  },

  /** Widgets this user's role may add. The server filters, not the client. */
  registry: (): Promise<{ widgets: WidgetDescriptor[] }> => api.get('/dashboards/registry'),

  create: (body: { name: string; description?: string; scope?: string; fromId?: number }) =>
    api.post('/dashboards', body),

  update: (id: number, body: Partial<Dashboard>) =>
    api.put(`/dashboards/${id}`, body).then((r: any) => r?.data ?? r),

  remove: (id: number) => api.delete(`/dashboards/${id}`).then((r: any) => r?.data ?? r),

  /**
   * Save the whole layout in one call. The server applies it in a single
   * transaction, so a failure leaves the previous layout intact rather than
   * a half-moved board.
   */
  saveLayout: (id: number, payload: LayoutSavePayload) =>
    api.put(`/dashboards/${id}/layout`, payload).then((r: any) => r?.data ?? r),

  addWidget: (id: number, body: { widgetKey: string; gridX?: number; gridY?: number; gridW?: number; gridH?: number; config?: any }) =>
    api.post(`/dashboards/${id}/widgets`, body),

  updateWidget: (id: number, widgetId: number, body: any) =>
    api.patch(`/dashboards/${id}/widgets/${widgetId}`, body).then((r: any) => r?.data ?? r),

  removeWidget: (id: number, widgetId: number) =>
    api.delete(`/dashboards/${id}/widgets/${widgetId}`).then((r: any) => r?.data ?? r),

  setDefault: (id: number) => api.post(`/dashboards/${id}/set-default`, {}),

  getPreferences: (): Promise<UserPreferences> => api.get('/dashboards/me/preferences'),

  savePreferences: (body: Partial<UserPreferences>) =>
    api.put('/dashboards/me/preferences', body).then((r: any) => r?.data ?? r),

  getFavorites: (): Promise<{ favorites: Favorite[] }> => api.get('/dashboards/me/favorites'),

  addFavorite: (body: { entityType: string; entityId?: number; label: string; link: string }) =>
    api.post('/dashboards/me/favorites', body),

  removeFavorite: (id: number) =>
    api.delete(`/dashboards/me/favorites/${id}`).then((r: any) => r?.data ?? r),
};

export default dashboardService;
